import type { OnChanges, OnInit } from '@angular/core'
import {
  Directive,
  ElementRef,
  HostListener,
  Renderer2,
  inject,
  input,
} from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'

import type { FormatAuthorNameFields } from '../pipes/format-author-name.pipe'
import { FormatAuthorNamePipe } from '../pipes/format-author-name.pipe'

// Extend the Person type to include our custom linkType property
type Person = FormatAuthorNameFields & {
  id?: number
  type?: 'author' | 'editor'
  linkType?: 'author' | 'editor'
}

@Directive({
  selector: '[dhpPersonLink]',
  providers: [FormatAuthorNamePipe],
})
export class PersonLinkDirective implements OnInit, OnChanges {
  private renderer = inject(Renderer2)
  private el = inject<ElementRef<HTMLAnchorElement>>(ElementRef)
  private router = inject(Router)
  private route = inject(ActivatedRoute)
  private formatAuthorNamePipe = inject(FormatAuthorNamePipe)

  readonly person = input.required<Person>({ alias: 'dhpPersonLink' })
  readonly showLink = input(true)
  readonly context = input<'author' | 'editor' | null>()

  // Current page context
  private currentContext: 'author' | 'editor' | null = null
  private currentId: number | null = null

  ngOnChanges(): void {
    // `context` is optional, so it may be absent from `SimpleChanges`; read the
    // signal input directly instead of indexing into the changes object.
    const context = this.context()
    if (context) {
      this.currentContext = context
      if (this.showLink()) {
        this.setHref()
      }
    }
  }

  // Prevent default navigation and handle it ourselves
  @HostListener('click', ['$event'])
  onClick(event: MouseEvent): void {
    // Always prevent default navigation and stop propagation
    event.preventDefault()
    event.stopPropagation()

    const person = this.person()
    if (this.showLink() && person.id) {
      // If we're already on this person's page, don't navigate
      if (this.currentId && person.id === this.currentId) {
        return
      }

      const routerLink = this.getRouterLink(person)

      // Create the URL for navigation
      const url = this.router.createUrlTree(routerLink).toString()

      window.location.href = url
    }
  }

  ngOnInit(): void {
    this.determineCurrentContext()

    const person = this.person()
    if (!person.id) {
      return
    }

    if (this.showLink()) {
      // Get router link even if this is the current person

      // Always set href attribute for proper styling and accessibility
      this.renderer.addClass(this.el.nativeElement, 'hover:underline')
      this.setHref()
    }

    this.renderer.addClass(this.el.nativeElement, 'text-primary')

    // Set the inner text using the pipe for consistent formatting
    const personName = this.formatAuthorNamePipe.transform(person)
    this.el.nativeElement.textContent = personName
  }

  private setHref(): void {
    const routerLink = this.getRouterLink(this.person())
    this.renderer.setAttribute(
      this.el.nativeElement,
      'href',
      this.router.createUrlTree(routerLink).toString()
    )
  }

  private determineCurrentContext(): void {
    // First check URL as it's most reliable
    const url = this.router.url
    if (url.includes('/editors/')) {
      this.currentContext = 'editor'

      // Extract ID from URL
      const matches = /\/editors\/(\d+)/.exec(url)
      if (matches?.[1]) {
        this.currentId = parseInt(matches[1], 10)
      }
      return
    } else if (url.includes('/authors/')) {
      this.currentContext = 'author'

      // Extract ID from URL
      const matches = /\/authors\/(\d+)/.exec(url)
      if (matches?.[1]) {
        this.currentId = parseInt(matches[1], 10)
      }
      return
    }

    // Fallback to route data if URL check fails
    let currentRoute = this.route
    while (currentRoute) {
      // Check if this route has a 'type' data property
      if (currentRoute.snapshot.data['type']) {
        this.currentContext = currentRoute.snapshot.data['type']

        // Get the ID parameter if present
        const idParam = currentRoute.snapshot.paramMap.get('id')
        if (idParam) {
          this.currentId = parseInt(idParam, 10)
        }
        break
      }

      // Move to parent route if available
      if (currentRoute.parent) {
        currentRoute = currentRoute.parent
      } else {
        break
      }
    }
  }

  private getRouterLink(person: Person): string[] {
    // If we're clicking on the same person we're currently viewing
    // if (this.currentId && person.id === this.currentId && this.currentContext) {
    //   // Don't navigate at all - just stay on current page (maintain current context)
    //   return [this.currentContext === 'author' ? '/authors' : '/editors', String(person.id)];
    // }

    // For other people, use their specific type (critical for correct navigation)
    let personType = person.linkType

    // If no explicit linkType, check the native type property of the person
    if (!personType && person.type) {
      personType = person.type
    }

    // If still no type determined, default to 'author'
    personType ??= 'author'

    // Now handle navigation with correct person type
    if ((this.currentContext ?? personType) === 'author') {
      return ['/authors', String(person.id)]
    } else {
      return ['/editors', String(person.id)]
    }
  }
}
